import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Button } from '../ui/button';
import { StockFilter } from '../StockFilter';
import { TimeSeries, StockFilter as StockFilterType } from '../../lib/types';

interface StockLevelChartProps {
  data: TimeSeries[];
  salesData?: TimeSeries[];
  granularData?: Array<{ id: string; name: string; data: TimeSeries[]; color: string }>;
  viewMode: 'average' | 'granular';
  onViewModeChange: (mode: 'average' | 'granular') => void;
  stockFilter: StockFilterType;
  onStockFilterChange: (filter: StockFilterType) => void;
  operators?: Array<{ id: string; name: string }>;
  machines?: Array<{ id: string; name: string }>;
  products?: Array<{ id: string; name: string }>;
  className?: string;
}


export function StockLevelChart({ 
  data, 
  salesData = [],
  granularData = [], 
  viewMode, 
  onViewModeChange, 
  stockFilter, 
  onStockFilterChange, 
  operators = [], 
  machines = [], 
  products = [], 
  className = '' 
}: StockLevelChartProps) {

  const formatXAxis = (tickItem: string) => {
    const date = new Date(tickItem);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTooltip = (value: number, name: string) => {
    if (name === 'Total Sales') {
      return `${Math.round(value)} units`;
    }
    return `${value.toFixed(1)}%`;
  };

  const hasGranularData = granularData.length > 0;

  // Prepare data for average view - combine stock and sales data
  const prepareAverageChartData = () => {
    if (!salesData || salesData.length === 0) {
      return data.map(point => ({ ...point, stockLevel: point.value }));
    }

    // Merge stock and sales data by timestamp
    const mergedData = data.map(stockPoint => {
      const salesPoint = salesData.find(s => s.timestamp === stockPoint.timestamp);
      return {
        timestamp: stockPoint.timestamp,
        stockLevel: stockPoint.value,
        sales: salesPoint?.value || null
      };
    });
    return mergedData;
  };

  // Prepare data for granular view
  const prepareGranularChartData = () => {
    if (!granularData.length) return [];

    // Get all unique timestamps
    const allTimestamps = new Set<string>();
    granularData.forEach(series => {
      series.data.forEach(point => allTimestamps.add(point.timestamp));
    });

    // Create combined data points
    const timestamps = Array.from(allTimestamps).sort();
    return timestamps.map(timestamp => {
      const dataPoint: any = { timestamp };
      granularData.forEach(series => {
        const point = series.data.find(p => p.timestamp === timestamp);
        dataPoint[series.id] = point?.value || null;
      });
      return dataPoint;
    });
  };

  const averageChartData = prepareAverageChartData();
  const granularChartData = viewMode === 'granular' ? prepareGranularChartData() : [];

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Stock Level Over Time</h3>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'average' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('average')}
          >
            Overall
          </Button>
          <Button
            variant={viewMode === 'granular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('granular')}
            disabled={!hasGranularData}
          >
            Granular
          </Button>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {viewMode === 'average' || !hasGranularData ? (
            <LineChart data={averageChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left"
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
              />
              {salesData && salesData.length > 0 && (
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value}`}
                />
              )}
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={formatTooltip}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  opacity: 1
                }}
                wrapperStyle={{
                  outline: 'none'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="stockLevel" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                name="Stock Level"
                animationDuration={3000}
                animationEasing="ease-out"
              />
              {salesData && salesData.length > 0 && (
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  name="Total Sales"
                  animationDuration={3000}
                  animationEasing="ease-out"
                />
              )}
            </LineChart>
          ) : (
            <LineChart data={granularChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={formatTooltip}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  opacity: 1
                }}
                wrapperStyle={{
                  outline: 'none'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              {granularData.map((series) => (
                <Line 
                  key={series.id}
                  type="monotone" 
                  dataKey={series.id}
                  stroke={series.color}
                  strokeWidth={2}
                  dot={{ strokeWidth: 2, r: 3 }}
                  name={series.name}
                  connectNulls
                  animationDuration={3000}
                  animationEasing="ease-out"
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {viewMode === 'granular' && hasGranularData && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Comparing {granularData.length} series:</p>
          <div className="flex flex-wrap gap-3">
            {granularData.map((series) => (
              <div key={series.id} className="flex items-center gap-2">
                <div 
                  className="w-4 h-0.5 rounded"
                  style={{ backgroundColor: series.color }}
                />
                <span className="text-xs text-gray-700">{series.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stock Filter - now inside the same container */}
      <StockFilter 
        filter={stockFilter}
        onFilterChange={onStockFilterChange}
        operators={operators}
        machines={machines}
        products={products}
      />
    </div>
  );
}